/**
 * Deterministic seed for `DemoFhirClient` (PROD-642 Sprint 2.2).
 *
 * Pre-loads the demo store with the canonical pathway fixtures so the
 * read-through cache in `pathway.store` resolves on the first hit
 * without a network call. The seed is deterministic + byte-stable so
 * HC1 reproducibility holds across reads.
 *
 * Implementation note: the seed does NOT call `createPlanDefinition` on
 * the demo client (that path mints fresh ids). It directly hands the
 * pre-built resource to `seedResource` with the canonical id from
 * `pathwayIdFor()`. This way the demo store's id space matches the
 * pathway store's `pathwayIdFor` mapping deterministically.
 *
 * HC5: zero em / en dashes anywhere.
 */
// Importing only from leaf modules (pathway-ids / pathway-types) keeps
// the seed below the Zustand store in the dependency graph. The
// pathway store imports the FHIR client surface (which transitively
// loads this seed) so the seed must NOT reach back into the store.
import { tlp1KhalidLabBasketItems, tlp1Step1NbaCandidates } from '../../data/synthetic-pathway-states';
import { pathwayToPlanDefinition } from '../fhir-mappers/pathway-to-plan-definition';
import type { DemoFhirClient } from './demo-client';
import type { EditablePathway } from '../../state/pathway-types';
import {
  PATHWAY_IDS,
  pathwayIdFor,
  type PathwayId,
} from '../../state/pathway-ids';
import { withTenantId } from './extensions';

const APEX = 'apex-cardiometabolic';
const LHI = 'longevity-health-institute';

/**
 * Default seeded recorded timestamp. Deterministic so the demo
 * fixtures are byte-stable across calls. The HC1 reproducibility
 * guarantee relies on this being a constant.
 */
export const DEMO_SEED_RECORDED = '2026-05-07T00:00:00.000Z';

/**
 * Build the canonical Cardiometabolic pathway fixture (Sprint 4.3.7-fix-2
 * 3-tier shape: Track + 4 Steps with category pills + the legacy
 * activity nodes wired as Actions inside their containing Step). Mirror
 * of `buildCardiometabolicPathway` in `pathway.store.ts` - kept in
 * parallel so the seed module stays below the Zustand store in the
 * dependency graph.
 */
function buildCardiometabolicPathway(tenantId: string): EditablePathway {
  const TRACK_ID = 'tlp1-track-1';
  const STEP_BASELINE = 'tlp1-step-baseline';
  const STEP_REVIEW = 'tlp1-step-review';
  const STEP_PROGRAM = 'tlp1-step-program';
  const STEP_COMPLETION = 'tlp1-step-completion';
  return {
    id: 'tlp1-glp1-v1',
    pathwayId: 'cardiometabolic-pathway',
    tenantId,
    nodes: [
      {
        id: TRACK_ID,
        nodeType: 'track',
        label: 'Cardiometabolic Pathway',
        description:
          'Apex Healthcare GLP-1 enrollment pathway. Triggered manually by the clinician at consult.',
        triggerType: 'manual',
      },
      {
        id: STEP_BASELINE,
        nodeType: 'step',
        label: 'Baseline Visit',
        description: 'Demographics, vitals, baseline lab order.',
        parentNodeId: TRACK_ID,
        stepCategory: 'baseline',
        stepCategoryLabel: 'Baseline',
      },
      {
        id: STEP_REVIEW,
        nodeType: 'step',
        label: 'Step 3 enrollment gate',
        description:
          'Review Gate for GLP-1 enrollment readiness. Linked NBAs surface in the inspector.',
        parentNodeId: TRACK_ID,
        isDecision: true,
        stepCategory: 'review',
        stepCategoryLabel: 'Review Gate',
      },
      {
        id: STEP_PROGRAM,
        nodeType: 'step',
        label: '16 Week Program',
        description: 'GLP-1 enrollment + lab follow-up + PROM dispatch.',
        parentNodeId: TRACK_ID,
        stepCategory: 'program',
        stepCategoryLabel: '16 Week Program',
        programDuration: '16 Week Program',
      },
      {
        id: STEP_COMPLETION,
        nodeType: 'step',
        label: 'Outcome',
        description: '16-week Cardiometabolic Pathway GLP-1 enrollment outcome.',
        parentNodeId: TRACK_ID,
        stepCategory: 'completion',
        stepCategoryLabel: 'Completion',
      },
      {
        id: 'tlp1-node-demographics',
        nodeType: 'demographics',
        label: 'Demographics',
        description: 'Age, sex, BMI, tenant context.',
        parentNodeId: STEP_BASELINE,
      },
      {
        id: 'tlp1-node-vitals',
        nodeType: 'vitals',
        label: 'Vitals',
        description: 'BMI, BP, HR baseline.',
        parentNodeId: STEP_BASELINE,
      },
      {
        id: 'tlp1-node-labs',
        nodeType: 'labs',
        label: 'Lab basket',
        requiresConfirmation: true,
        labBasketItems: tlp1KhalidLabBasketItems.map((item) => ({ ...item })),
        description:
          'Cardiometabolic Pathway baseline lab basket. TSH stays preselected per ADR-001.',
        parentNodeId: STEP_BASELINE,
      },
      {
        id: 'tlp1-node-decision',
        nodeType: 'decision',
        label: 'Step 3 enrollment gate',
        stateMachine:
          'pending -> awaiting_labs -> labs_confirmed -> ready_to_enroll. eGFR < 60 routes to endo referral.',
        linkedNbaIds: tlp1Step1NbaCandidates.map((c) => c.id),
        description:
          'Decision Point for GLP-1 enrollment readiness. Linked NBAs surface in the inspector.',
        parentNodeId: STEP_REVIEW,
      },
      {
        id: 'tlp1-node-nba-1',
        nodeType: 'nba',
        label: 'Order baseline lab panel',
        description: 'Tier 2 review window action.',
        parentNodeId: STEP_PROGRAM,
      },
      {
        id: 'tlp1-node-nba-2',
        nodeType: 'nba',
        label: 'Dispatch MSQ baseline PROM',
        description: 'Tier 2 dispatch via JITAI.',
        parentNodeId: STEP_PROGRAM,
      },
      {
        id: 'tlp1-node-nba-3',
        nodeType: 'nba',
        label: 'Refer to endocrinology for thyroid workup',
        description: 'Tier 1 manual approve.',
        parentNodeId: STEP_PROGRAM,
      },
      {
        id: 'tlp1-node-outcome',
        nodeType: 'outcome',
        label: 'Enrollment outcome',
        description: '16-week Cardiometabolic Pathway GLP-1 enrollment with reassessment.',
        parentNodeId: STEP_COMPLETION,
      },
    ],
    edges: [
      { id: 'tlp1-edge-track-baseline', source: TRACK_ID, target: STEP_BASELINE },
      { id: 'tlp1-edge-baseline-review', source: STEP_BASELINE, target: STEP_REVIEW },
      { id: 'tlp1-edge-review-program', source: STEP_REVIEW, target: STEP_PROGRAM },
      { id: 'tlp1-edge-program-completion', source: STEP_PROGRAM, target: STEP_COMPLETION },
      { id: 'tlp1-edge-demo-vitals', source: 'tlp1-node-demographics', target: 'tlp1-node-vitals' },
      { id: 'tlp1-edge-vitals-labs', source: 'tlp1-node-vitals', target: 'tlp1-node-labs' },
      { id: 'tlp1-edge-labs-decision', source: 'tlp1-node-labs', target: 'tlp1-node-decision' },
      { id: 'tlp1-edge-decision-nba-1', source: 'tlp1-node-decision', target: 'tlp1-node-nba-1' },
      { id: 'tlp1-edge-decision-nba-2', source: 'tlp1-node-decision', target: 'tlp1-node-nba-2' },
      { id: 'tlp1-edge-decision-nba-3', source: 'tlp1-node-decision', target: 'tlp1-node-nba-3' },
      { id: 'tlp1-edge-nba-1-outcome', source: 'tlp1-node-nba-1', target: 'tlp1-node-outcome' },
      { id: 'tlp1-edge-nba-2-outcome', source: 'tlp1-node-nba-2', target: 'tlp1-node-outcome' },
      { id: 'tlp1-edge-nba-3-outcome', source: 'tlp1-node-nba-3', target: 'tlp1-node-outcome' },
    ],
  };
}

/**
 * Build a placeholder pathway scaffold matching `buildPlaceholderPathway`
 * with the Sprint 4.3.7-fix-2 3-tier shape (Track + 4 Steps + Action-
 * shaped legacy activity nodes).
 */
function buildPlaceholderPathway(
  pathwayId: PathwayId,
  tenantId: string,
): EditablePathway {
  const id = pathwayIdFor(pathwayId);
  const TRACK_ID = `${id}-track-1`;
  const STEP_BASELINE = `${id}-step-baseline`;
  const STEP_REVIEW = `${id}-step-review`;
  const STEP_PROGRAM = `${id}-step-program`;
  const STEP_COMPLETION = `${id}-step-completion`;
  const isHrt = pathwayId === 'hrt-pathway';
  const baselineCategory = isHrt ? 'exclusion' : 'baseline';
  const baselineLabel = isHrt ? 'Exclusion' : 'Baseline';
  const trackLabel: Record<PathwayId, string> = {
    'cardiometabolic-pathway': 'Cardiometabolic Pathway',
    'hrt-pathway': 'HRT Pathway',
    'metabolic-syndrome-pathway': 'Metabolic Syndrome Pathway',
    'cvd-prevention-pathway': 'CVD Prevention Pathway',
  };
  return {
    id,
    pathwayId,
    tenantId,
    nodes: [
      { id: TRACK_ID, nodeType: 'track', label: trackLabel[pathwayId], description: `${pathwayId} placeholder Track.`, triggerType: 'manual' },
      { id: STEP_BASELINE, nodeType: 'step', label: isHrt ? 'Exclusion criteria' : 'Baseline Visit', description: `${pathwayId} ${baselineLabel.toLowerCase()} step.`, parentNodeId: TRACK_ID, stepCategory: baselineCategory, stepCategoryLabel: baselineLabel },
      { id: STEP_REVIEW, nodeType: 'step', label: 'Review Gate', description: `${pathwayId} review gate.`, parentNodeId: TRACK_ID, isDecision: true, stepCategory: 'review', stepCategoryLabel: 'Review Gate' },
      { id: STEP_PROGRAM, nodeType: 'step', label: 'Program', description: `${pathwayId} program enrollment.`, parentNodeId: TRACK_ID, stepCategory: 'program', stepCategoryLabel: 'Program' },
      { id: STEP_COMPLETION, nodeType: 'step', label: 'Completion', description: `${pathwayId} completion outcome.`, parentNodeId: TRACK_ID, stepCategory: 'completion', stepCategoryLabel: 'Completion' },
      { id: `${id}-node-demographics`, nodeType: 'demographics', label: 'Demographics', description: `${pathwayId} demographics scaffold.`, parentNodeId: STEP_BASELINE },
      { id: `${id}-node-vitals`, nodeType: 'vitals', label: 'Vitals', description: `${pathwayId} baseline vitals scaffold.`, parentNodeId: STEP_BASELINE },
      { id: `${id}-node-labs`, nodeType: 'labs', label: 'Lab basket', requiresConfirmation: true, labBasketItems: [], description: `${pathwayId} lab basket placeholder. Phase 2 fixture lands the real basket.`, parentNodeId: STEP_BASELINE },
      { id: `${id}-node-decision`, nodeType: 'decision', label: 'Decision Point', stateMachine: 'pending -> ready -> committed', linkedNbaIds: [], description: `${pathwayId} decision-point scaffold.`, parentNodeId: STEP_REVIEW },
      { id: `${id}-node-nba-1`, nodeType: 'nba', label: `${pathwayId} NBA placeholder`, description: 'Phase 2 fixture lands the verbatim NBA payload.', parentNodeId: STEP_PROGRAM },
      { id: `${id}-node-outcome`, nodeType: 'outcome', label: 'Outcome', description: `${pathwayId} projected outcome.`, parentNodeId: STEP_COMPLETION },
    ],
    edges: [
      { id: `${id}-edge-track-baseline`, source: TRACK_ID, target: STEP_BASELINE },
      { id: `${id}-edge-baseline-review`, source: STEP_BASELINE, target: STEP_REVIEW },
      { id: `${id}-edge-review-program`, source: STEP_REVIEW, target: STEP_PROGRAM },
      { id: `${id}-edge-program-completion`, source: STEP_PROGRAM, target: STEP_COMPLETION },
      { id: `${id}-edge-demo-vitals`, source: `${id}-node-demographics`, target: `${id}-node-vitals` },
      { id: `${id}-edge-vitals-labs`, source: `${id}-node-vitals`, target: `${id}-node-labs` },
      { id: `${id}-edge-labs-decision`, source: `${id}-node-labs`, target: `${id}-node-decision` },
      { id: `${id}-edge-decision-nba`, source: `${id}-node-decision`, target: `${id}-node-nba-1` },
      { id: `${id}-edge-nba-outcome`, source: `${id}-node-nba-1`, target: `${id}-node-outcome` },
    ],
  };
}

/**
 * Seed function passed to `DemoFhirClient` constructor. Loads the
 * canonical Apex / Cardiometabolic pathway plus placeholder scaffolds
 * for the other pathways and the LHI tenant so the editor surfaces
 * something for every (tenant, pathway) combination on first read.
 *
 * The seed populates the store directly via the public
 * `createPlanDefinition` surface. The demo client's `createGeneric`
 * is synchronous in its store mutation (the `async` keyword wraps the
 * return value, but no await happens before the bucket write). The
 * seed nevertheless calls the public method so the path mirrors live
 * behaviour: any future change that introduces real async work would
 * still run during boot.
 */
export function seedDemoFhirClient(client: DemoFhirClient): void {
  const tenants = [APEX, LHI];
  for (const tenantId of tenants) {
    for (const pathwayId of PATHWAY_IDS) {
      const pathway =
        tenantId === APEX && pathwayId === 'cardiometabolic-pathway'
          ? buildCardiometabolicPathway(tenantId)
          : buildPlaceholderPathway(pathwayId, tenantId);
      const planDefinition = withTenantId(
        pathwayToPlanDefinition(pathway),
        tenantId,
      );
      // Use the synchronous bucket-write helper exposed for the seed
      // so the store is populated before constructor return.
      client.seedPlanDefinitionSync(planDefinition, tenantId);
    }
  }
}
